import React from "react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Button, Container, Form, Modal, Card, Collapse } from "react-bootstrap";
import { Robot, Database, Globe } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { useGovernment } from "contexts/GovernmentContext";
import { getCredibilityColor } from "../../utils/resources";
import { getExternalFaqById, createFaq, updateResource } from "../../apis/ResourceApi";
import ReactMarkdown from "react-markdown";
import FAQRegisterDialog from "../faq/FAQRegisterDialog";
import { useDialog } from "../../contexts/DialogContext.tsx";
import { RESOURCE_STATUS } from "../../constants/status";
import { APP_TYPE, APP_TYPES } from "../../constants/app";
import { MESSAGES } from "../../lang/ja";

const propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onUpdate: PropTypes.func,
  question: PropTypes.string,
  answer: PropTypes.string,
  aiAnswer: PropTypes.string,
  credibility: PropTypes.string,
  credibilityReason: PropTypes.string,
  url: PropTypes.string,
  tag: PropTypes.string,
  chatProcessData: PropTypes.object,
  editable: PropTypes.bool,
};
const defaultProps = {
  isOpen: false,
  onClose: () => {},
  onUpdate: () => {},
  question: "",
  answer: "",
  aiAnswer: "",
  credibility: "",
  credibilityReason: "",
  url: "",
  tag: "",
  chatProcessData: {},
  editable: true,
};

const UpdateAnswerDialog = (props) => {
  const {
    isOpen,
    onClose,
    onUpdate,
    question,
    answer,
    aiAnswer,
    credibility,
    credibilityReason,
    url,
    tag,
    chatProcessData,
    editable,
  } = props;
  const [fixAnswer, setFixAnswer] = useState(answer);
  const [fixUrl, setFixUrl] = useState(url);
  const [fixTag, setFixTag] = useState(tag);
  const [validated, setValidated] = useState(false);
  const [exceededLimit, setExceededLimit] = useState(false);
  const { government } = useGovernment();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const { userInfo } = useSelector((state) => state.users);
  const governmentId = userInfo?.governmentId;
  const [loading, setLoading] = useState(false);
  const { success, error } = useDialog();
  const [isScrapingExpanded, setIsScrapingExpanded] = useState(false);

  useEffect(() => {
    setFixAnswer(answer);
  }, [answer]);

  useEffect(() => {
    setFixUrl(url);
  }, [url]);

  useEffect(() => {
    setFixTag(tag);
  }, [tag]);

  const handleFormSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() && !exceededLimit) {
      onUpdate(fixAnswer, fixUrl, fixTag);
      setFixAnswer("");
      setFixUrl("");
      onClose();
    }
    setValidated(true);
  };

  const onFixAnswerChange = (event) => {
    const inputValue = event.target.value;
    setFixAnswer(inputValue);
    setExceededLimit(inputValue.length > 2000);
    setValidated(event.target.form.checkValidity() && !exceededLimit);
  };

  const isButtonDisabled = () => {
    if (tag!==fixTag) return false;
    return !validated || !editable || exceededLimit;
  };

  const openFAQDialog = async (sourceId) => {
    console.log("sourceId", sourceId);
    try {
      const res = await getExternalFaqById(governmentId, sourceId);
      console.log("res", res);
      if (res.success) {
        setSelectedFaq(res.data);
      } else {
        console.error("Error fetching FAQ:", res.error);
        error("FAQの取得に失敗しました");
      }
    } catch (e) {
      console.error("Error fetching FAQ:", e);
      error("FAQの取得に失敗しました");
    }
  };

  const onRegisterFaq = async (question, answer, url, tag) => {
    if (loading) return;
    setLoading(true);
    let res;
    if (!selectedFaq.createdAt) {
      res = await createFaq(governmentId, question, answer, url, null, APP_TYPE === APP_TYPES.OUTSIDE_ADMIN, tag);
      console.log("createFaq", res);
    } else {
      const newProps = { name: question, answer, url, rejectReason: "", tag };
      if (selectedFaq.status !== RESOURCE_STATUS.PENDING) {
        newProps.status = RESOURCE_STATUS.PROCESS_REQUESTING;
      }
      res = await updateResource(selectedFaq, newProps);
    }
    if (res.success) {
      success(MESSAGES.REGISTERED_FAQ_SUCCESSFULLY || "FAQが正常に登録されました");
      setSelectedFaq(null);
    } else {
      error(MESSAGES.SOMETHING_WRONG || "何らかの問題が発生しました");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <Modal
      dialogClassName="custom-modal-position"
      show={isOpen}
      onHide={onClose}
      backdropClassName="modal show"
      size="lg"
    >
      <Form noValidate validated={validated} onSubmit={handleFormSubmit}>
        <Modal.Header closeButton onHide={onClose}>
          <Modal.Title className="">回答詳細</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "65vh", overflow: "auto" }}>
          <Container style={{ paddingBottom: "16px" }}>
            <Form.Label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>ユーザーの質問</Form.Label>
            <Form.Control plaintext readOnly defaultValue={question} />
          </Container>

          <Container style={{ paddingBottom: "24px" }}>
            <Form.Label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>AIの回答</Form.Label>
            <div
              className="form-control-plaintext"
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                minHeight: "150px",
                padding: "8px 12px",
                lineHeight: "1.5",
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: "4px",
              }}
            >
              {aiAnswer}
            </div>
          </Container>

          <Container style={{ paddingBottom: "16px" }}>
            <Form.Label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>回答の信頼度</Form.Label>
            <Form.Control
              plaintext
              readOnly
              defaultValue={credibility}
              style={{
                color: getCredibilityColor(credibility),
                fontWeight: "bold",
              }}
            />
          </Container>

          <Container style={{ paddingBottom: "24px" }}>
            <Form.Label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
              {credibility}理由
            </Form.Label>
            <div
              className="form-control-plaintext"
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                minHeight: "100px",
                padding: "8px 0",
                lineHeight: "1.5",
              }}
            >
              {credibilityReason}
            </div>
          </Container>

          <Container style={{ paddingBottom: "16px" }}>
            <Form.Label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>回答を修正する</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={fixAnswer}
              disabled={!editable}
              onChange={onFixAnswerChange}
              required
              isInvalid={exceededLimit}
            />
            <Form.Control.Feedback type="invalid">
              {exceededLimit && "回答は2000文字以下で入力してください。"}
            </Form.Control.Feedback>
          </Container>

          <Container style={{ paddingBottom: "16px" }}>
            <Form.Label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>URLを修正する</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={fixUrl}
              disabled={!editable}
              onChange={(e) => setFixUrl(e.target.value)}
              placeholder="回答を補完する参照情報がHPにある場合は、こちらにURLを貼り付けてください。AIが提示したURLが間違っていた場合は、こちらで修正しないと引き続き間違ったURLを提示します。"
              style={{
                "&::placeholder": {
                  color: "#444444",
                },
              }}
            />
          </Container>

          <Container style={{ paddingBottom: "24px" }}>
            <Form.Label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
              タグ
              <p style={{ marginTop: "4px", marginBottom: "12px" }}>
                <span
                  onClick={() => setFixTag("")}
                  style={{
                    color: "#dc3545",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.7rem",
                    transition: "all 0.2s ease",
                    display: "inline-block",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = "0.7";
                    e.target.style.textDecoration = "none";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = "1";
                    e.target.style.textDecoration = "underline";
                    e.target.style.color = "#dc3545";
                  }}
                  onMouseDown={(e) => {
                    e.target.style.color = "#007bff";
                    e.target.style.transform = "scale(0.95)";
                  }}
                  onMouseUp={(e) => {
                    e.target.style.color = "#dc3545";
                    e.target.style.transform = "scale(1)";
                  }}
                >
                  (タグを外し、未分類にする場合はここをクリック)
                </span>
              </p>
            </Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {(government?.departments ?? [])
                .filter((d) => !d.includes("#invisible"))
                .map((department) => {
                  const pos = department.indexOf("#");
                  const departmentName = pos >= 0 ? department.substring(0, pos) : department;
                  return (
                    <div key={departmentName} className="position-relative" style={{ minWidth: "fit-content" }}>
                      <Form.Check
                        type="radio"
                        id={`radio-${departmentName}`}
                        name="department-radio"
                        checked={fixTag === departmentName}
                        onChange={() => setFixTag(departmentName)}
                        className="d-none"
                      />
                      <label
                        htmlFor={`radio-${departmentName}`}
                        className="px-3 py-1 rounded-pill"
                        style={{
                          border: "1px solid #dee2e6",
                          backgroundColor: fixTag === departmentName ? "#007bff" : "#ffffff",
                          color: fixTag === departmentName ? "#ffffff" : "#6c757d",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          fontSize: "0.9rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {departmentName}
                      </label>
                    </div>
                  );
                })}
            </div>
          </Container>

          <Container style={{ paddingBottom: "24px" }}>
            <Card style={{ border: "none", backgroundColor: "transparent" }}>
              <Card.Header
                style={{
                  cursor: "pointer",
                  padding: "12px 0",
                  backgroundColor: "transparent",
                  border: "none",
                }}
                onClick={() => setIsAccordionOpen(!isAccordionOpen)}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span
                    style={{
                      fontWeight: "400",
                      fontSize: "16px",
                      color: "#6c757d",
                      borderTop: "1px solid #dee2e6",
                      borderBottom: "1px solid #dee2e6",
                      padding: "8px 0",
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    {isAccordionOpen ? "▼" : "▶"}検証
                  </span>
                </div>
              </Card.Header>
              <Collapse in={isAccordionOpen}>
                <Card.Body style={{ padding: "0", paddingTop: "12px" }}>
                  <Form.Label
                    style={{
                      color: "black",
                      fontWeight: "700",
                      marginBottom: "16px",
                      display: "block",
                      fontSize: "16px",
                    }}
                  >
                    回答処理の流れ
                  </Form.Label>
                  <div>
                    {/* 1. 質問の言い換えブロック */}
                    <div style={{
                      border: "2px solid #e9ecef",
                      borderRadius: "8px",
                      padding: "0",
                      marginBottom: "16px",
                      backgroundColor: "#ffffff"
                    }}>
                      <div style={{
                        backgroundColor: "#f8f9fa",
                        padding: "12px 16px",
                        marginBottom: "0",
                        borderRadius: "6px 6px 0 0",
                        borderBottom: "1px solid #e9ecef"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: "white",
                              border: "1px solid #dc3545",
                              borderRadius: "50%",
                              color: "#dc3545",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              marginRight: "8px",
                            }}
                          >
                            <Robot size={12} />
                          </div>
                          <span style={{ fontWeight: "500", fontSize: "14px" }}>質問の言い換え</span>
                        </div>
                        <div style={{ fontSize: "12px", color: "#6c757d" }}>
                          ベクトル検索に適した質問に変換します
                        </div>
                      </div>
                      <div style={{ padding: "16px" }}>
                        <span style={{ fontSize: "14px" }}>
                          {chatProcessData?.vectorSearchQuery}
                        </span>
                      </div>
                    </div>

                    {/* 矢印 */}
                    <div style={{
                      textAlign: "center",
                      marginBottom: "16px",
                      fontSize: "20px",
                      color: "#6c757d"
                    }}>
                      ↓
                    </div>

                    {/* 2. RAG検索結果ブロック */}
                    <div style={{
                      border: "2px solid #e9ecef",
                      borderRadius: "8px",
                      padding: "0",
                      marginBottom: "16px",
                      backgroundColor: "#ffffff"
                    }}>
                      <div style={{
                        backgroundColor: "#f8f9fa",
                        padding: "12px 16px",
                        marginBottom: "0",
                        borderRadius: "6px 6px 0 0",
                        borderBottom: "1px solid #e9ecef"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: "#6c757d",
                              borderRadius: "50%",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              marginRight: "8px",
                            }}
                          >
                            <Database size={12} />
                          </div>
                          <span style={{ fontWeight: "500", fontSize: "14px" }}>RAG検索結果</span>
                        </div>
                        <div style={{ fontSize: "12px", color: "#6c757d" }}>
                          しきい値以上の検索結果がAIの回答に採用されます
                        </div>
                      </div>
                      <div style={{ padding: "16px" }}>
                        <div style={{ marginLeft: "0" }}>
                          <span style={{ fontSize: "12px", color: "#6c757d" }}>しきい値</span>
                        </div>

                        <div style={{ marginBottom: "8px", marginLeft: "0" }}>
                          <span style={{ fontSize: "14px", color: "#6c757d", fontWeight: "bold" }}>
                            FAQ：{government?.searchConditions?.similarityExternalFaqMinRate} | ファイル：
                            {government?.searchConditions?.similarityExternalBaseMinRate}
                          </span>
                        </div>

                        {/* FAQ検索結果 */}
                        {chatProcessData?.faqRagData && chatProcessData.faqRagData?.length > 0 && (
                          <div style={{ marginLeft: "0", marginBottom: "20px" }}>
                            <div style={{ marginBottom: "12px" }}>
                              <span style={{ fontWeight: "500", fontSize: "13px", color: "#495057" }}>FAQ</span>
                            </div>
                            <div style={{ marginLeft: "16px" }}>
                              {chatProcessData.faqRagData.map((item, index) => (
                                <div
                                  key={index}
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    padding: "12px 0",
                                    borderBottom:
                                      index < chatProcessData.faqRagData.length - 1 ? "1px solid #e9ecef" : "none",
                                  }}
                                >
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                                      <span
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          padding: "4px 8px",
                                          borderRadius: "4px",
                                          fontSize: "12px",
                                          marginRight: "12px",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        類似度：{item?.metadata?.similarity}
                                      </span>
                                    </div>
                                    <div style={{ marginBottom: "8px" }}>
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#6c757d",
                                          display: "block",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        質問
                                      </span>
                                      <span style={{ fontSize: "14px", display: "block" }}>
                                        {item?.metadata?.question}
                                      </span>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#6c757d",
                                          display: "block",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        回答
                                      </span>
                                      <span style={{ fontSize: "14px", display: "block" }}>{item?.metadata?.answer}</span>
                                    </div>
                                  </div>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    style={{ fontSize: "12px", padding: "4px 8px", marginLeft: "16px", flexShrink: 0 }}
                                    onClick={() => {
                                      openFAQDialog(item.metadata.source_id);
                                    }}
                                  >
                                    詳細をみる 🔗
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ファイル検索結果 */}
                        {chatProcessData?.fileRagData && chatProcessData.fileRagData?.length > 0 && (
                          <div style={{ marginLeft: "0" }}>
                            <div style={{ marginBottom: "12px" }}>
                              <span style={{ fontWeight: "500", fontSize: "13px", color: "#495057" }}>ファイル</span>
                            </div>
                            <div style={{ marginLeft: "16px" }}>
                              {chatProcessData.fileRagData.map((item, index) => (
                                <div
                                  key={index}
                                  style={{
                                    padding: "12px 0",
                                    borderBottom:
                                      index < chatProcessData.fileRagData.length - 1 ? "1px solid #e9ecef" : "none",
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                                    <span
                                      style={{
                                        backgroundColor: "#f8f9fa",
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        fontSize: "12px",
                                        marginRight: "12px",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      類似度：{item?.metadata?.similarity || "N/A"}
                                    </span>
                                  </div>
                                  <div style={{ marginBottom: "8px" }}>
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#6c757d",
                                        display: "block",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      内容
                                    </span>
                                    <span style={{ fontSize: "14px", display: "block" }}>{item?.pageContent}</span>
                                  </div>
                                  {item?.metadata?.title && (
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#6c757d",
                                          display: "block",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        ファイル名
                                      </span>
                                      <span style={{ fontSize: "14px", display: "block" }}>{item?.metadata?.title}</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 矢印 */}
                    <div style={{
                      textAlign: "center",
                      marginBottom: "16px",
                      fontSize: "20px",
                      color: "#6c757d"
                    }}>
                      ↓
                    </div>

                    {/* 3. Google検索結果ブロック */}
                    <div style={{
                      border: "2px solid #e9ecef",
                      borderRadius: "8px",
                      padding: "0",
                      marginBottom: "16px",
                      backgroundColor: "#ffffff"
                    }}>
                      <div style={{
                        backgroundColor: "#f8f9fa",
                        padding: "12px 16px",
                        marginBottom: "0",
                        borderRadius: "6px 6px 0 0",
                        borderBottom: "1px solid #e9ecef"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: "#4285f4",
                              borderRadius: "50%",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              marginRight: "8px",
                            }}
                          >
                            <Globe size={12} />
                          </div>
                          <span style={{ fontWeight: "500", fontSize: "14px" }}>Google検索結果</span>
                        </div>
                        <div style={{ fontSize: "12px", color: "#6c757d" }}>
                          RAG検索結果でしきい値以上の文章がなかった場合、Google検索が実行されます
                        </div>
                      </div>
                      <div style={{ padding: "16px" }}>
                        <div style={{ marginLeft: "0", fontSize: "14px" }}>
                          {chatProcessData?.googleSearchItems &&
                            chatProcessData?.googleSearchItems.map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  marginBottom: "12px",
                                  paddingBottom: "12px",
                                  borderBottom:
                                    index < chatProcessData.googleSearchItems.length - 1 ? "1px solid #e9ecef" : "none",
                                }}
                              >
                                <a
                                  href={item.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#007bff",
                                    textDecoration: "none",
                                    fontSize: "14px",
                                  }}
                                >
                                  {item.link}
                                </a>
                                <div style={{ fontWeight: "500", margin: "4px 0", color: "#1a0dab" }}>{item.title}</div>
                                <div style={{ color: "#6c757d", fontSize: "13px", lineHeight: "1.4" }}>
                                  {item.snippet}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>

                    {/* 矢印 */}
                    <div style={{
                      textAlign: "center",
                      marginBottom: "16px",
                      fontSize: "20px",
                      color: "#6c757d"
                    }}>
                      ↓
                    </div>

                    {/* 4. スクレイピング結果ブロック */}
                    <div style={{
                      border: "2px solid #e9ecef",
                      borderRadius: "8px",
                      padding: "0",
                      backgroundColor: "#ffffff"
                    }}>
                      <div style={{
                        backgroundColor: "#f8f9fa",
                        padding: "12px 16px",
                        marginBottom: "0",
                        borderRadius: "6px 6px 0 0",
                        borderBottom: "1px solid #e9ecef"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: "#4285f4",
                              borderRadius: "50%",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              marginRight: "8px",
                            }}
                          >
                            <Globe size={12} />
                          </div>
                          <span style={{ fontWeight: "500", fontSize: "14px" }}>スクレイピング結果</span>
                        </div>
                        <div style={{ fontSize: "12px", color: "#6c757d" }}>
                          Google検索結果を元に各ページの内容を抽出します。                        </div>
                      </div>
                      <div style={{ padding: "16px" }}>
                        <div style={{ marginLeft: "0", fontSize: "14px" }}>
                          <div
                            style={{
                              color: "#6c757d",
                              fontSize: "13px",
                              lineHeight: "1.4",
                              ...(isScrapingExpanded ? {} : {
                                maxHeight: "calc(1.4em * 3)", // 3行分の高さ
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                              })
                            }}
                          >
                            <ReactMarkdown
                              components={{
                                a: ({ href, children, ...props }) => (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "#007bff", textDecoration: "underline" }}
                                    {...props}
                                  >
                                    {children}
                                  </a>
                                ),
                                code: ({ children, ...props }) => (
                                  <code
                                    style={{
                                      backgroundColor: "#f8f9fa",
                                      padding: "2px 4px",
                                      borderRadius: "3px",
                                      fontFamily: "monospace",
                                      fontSize: "12px",
                                    }}
                                    {...props}
                                  >
                                    {children}
                                  </code>
                                ),
                              }}
                            >
                              {chatProcessData?.searchResultsText}
                            </ReactMarkdown>
                          </div>
                          {chatProcessData?.searchResultsText && chatProcessData?.searchResultsText.length > 0 && (
                            <div style={{ textAlign: "center", marginTop: "8px" }}>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => setIsScrapingExpanded(!isScrapingExpanded)}
                                style={{
                                  padding: "4px 0",
                                  fontSize: "14px",
                                  textDecoration: "none",
                                  color: "#007bff",
                                }}
                              >
                                {isScrapingExpanded ? "閉じる" : "もっと見る"}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Collapse>
            </Card>
          </Container>
        </Modal.Body>
        <FAQRegisterDialog
          isOpen={!!selectedFaq}
          faq={selectedFaq}
          tag={selectedFaq?.tag}
          onClose={() => {
            setSelectedFaq(null);
          }}
          onRegister={onRegisterFaq}
        />
        <Modal.Footer className="d-flex justify-content-end">
          <Button className="btn-outline btn-wd mr-1" variant="default" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            className="btn-outline btn-wd mr-1"
            variant="default"
            disabled={isButtonDisabled()}
            onClick={() => {
              onUpdate(fixAnswer, fixUrl, fixTag);
            }}
          >
            保存
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

UpdateAnswerDialog.propTypes = propTypes;
UpdateAnswerDialog.defaultProps = defaultProps;
export default UpdateAnswerDialog;
